import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from '../utils/api';
import Quill from 'quill';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import 'quill/dist/quill.snow.css';
import { createTargetImageFile, TARGET_IMAGE_HEIGHT, TARGET_IMAGE_WIDTH } from '../utils/imageProcessing';
import './ProductManager.css';

const ProductManager = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [gstRates, setGstRates] = useState([]);
  const [, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [variantError, setVariantError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    onConfirm: null,
  });

  // Filter and search states
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSubcategory, setFilterSubcategory] = useState('');
  const [filterSKU, setFilterSKU] = useState('');
  const [searchText, setSearchText] = useState('');

  // Compute filtered products
  const filteredProducts = products.filter(product => {
    // Category filter
    if (filterCategory && product.category_id !== filterCategory) return false;
    // Subcategory filter
    if (filterSubcategory && product.subcategory_id !== filterSubcategory) return false;
    // SKU filter (partial match)
    if (filterSKU && !product.source_sku.toLowerCase().includes(filterSKU.toLowerCase())) return false;
    // Name search (partial match)
    if (searchText && !product.name.toLowerCase().includes(searchText.toLowerCase())) return false;
    return true;
  });

  // Form states
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showColorManager, setShowColorManager] = useState(false);

  // Form inputs
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    source_sku: '',
    category_id: '',
    subcategory_id: '',
    tags: '',
    rate_type: '',
    material: '',
    return_policy_override: ''
  });

  // Color and image management
  const [productColors, setProductColors] = useState([]);
  const [selectedColor, setSelectedColor] = useState('');
  const [uploadingImages, setUploadingImages] = useState(false);
  const [activeCameraColorId, setActiveCameraColorId] = useState(null);
  const [cameraDevices, setCameraDevices] = useState([]);
  const [selectedCameraDeviceId, setSelectedCameraDeviceId] = useState('');
  const [capturedImageSourceUrl, setCapturedImageSourceUrl] = useState('');
  const [capturedImagePreviewUrl, setCapturedImagePreviewUrl] = useState('');
  const [capturedImageFile, setCapturedImageFile] = useState(null);
  const [capturedImageColorId, setCapturedImageColorId] = useState('');
  const [rotationDegrees, setRotationDegrees] = useState(0);
  const [cropXPercent, setCropXPercent] = useState(0);
  const [cropYPercent, setCropYPercent] = useState(0);
  const [cropWidthPercent, setCropWidthPercent] = useState(100);
  const [cropHeightPercent, setCropHeightPercent] = useState(100);
  const [cropDragState, setCropDragState] = useState(null);
  const [strictBlurMode, setStrictBlurMode] = useState(false);
  const [captureCountdown, setCaptureCountdown] = useState(null);
  const [cameraHint, setCameraHint] = useState('');
  const [imageQuality, setImageQuality] = useState(null);
  const [cameraError, setCameraError] = useState('');
  const [cropModalState, setCropModalState] = useState({
    open: false,
    productColorId: null,
    sourceFile: null,
    previewUrl: '',
  });
  const [processedImagePreview, setProcessedImagePreview] = useState(null);
  const [imageUploadSettings, setImageUploadSettings] = useState({
    width: 2000,
    height: 2000,
    maxSizeMB: 0.5,
    compressionPercent: 80,
  });
  const [imageCrop, setImageCrop] = useState({
    unit: '%',
    x: 0,
    y: 0,
    width: 100,
    height: 100,
    aspect: 1,
  });

  // Quill Editor refs
  const quillContainerRef = useRef(null);
  const quillInstance = useRef(null);
  const cameraVideoRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const cameraBusyRef = useRef(false);
  const captureTimerRef = useRef(null);
  const cropStageRef = useRef(null);
  const capturedSourceUrlRef = useRef('');
  const capturedPreviewUrlRef = useRef('');

  const MIN_CAPTURE_WIDTH = 1000;
  const MIN_CAPTURE_HEIGHT = 700;
  const BLUR_WARNING_THRESHOLD = 40;
  const MIN_CROP_PERCENT = 15;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const normalizeRotation = (value) => {
    const normalized = value % 360;
    return normalized < 0 ? normalized + 360 : normalized;
  };

  const loadImageFromUrl = (url) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load captured image.'));
    img.src = url;
  });

  const calculateBlurScore = (canvas) => {
    const maxDimension = 320;
    const scale = Math.min(1, maxDimension / Math.max(canvas.width, canvas.height));
    const w = Math.max(8, Math.floor(canvas.width * scale));
    const h = Math.max(8, Math.floor(canvas.height * scale));

    const sampleCanvas = document.createElement('canvas');
    sampleCanvas.width = w;
    sampleCanvas.height = h;
    const sampleCtx = sampleCanvas.getContext('2d');
    if (!sampleCtx) return 0;

    sampleCtx.drawImage(canvas, 0, 0, w, h);
    const imageData = sampleCtx.getImageData(0, 0, w, h).data;
    const gray = new Float32Array(w * h);

    for (let i = 0; i < w * h; i += 1) {
      const offset = i * 4;
      const r = imageData[offset];
      const g = imageData[offset + 1];
      const b = imageData[offset + 2];
      gray[i] = (0.299 * r) + (0.587 * g) + (0.114 * b);
    }

    let sum = 0;
    let sumSq = 0;
    let count = 0;

    for (let y = 1; y < h - 1; y += 1) {
      for (let x = 1; x < w - 1; x += 1) {
        const idx = (y * w) + x;
        const gx = gray[idx + 1] - gray[idx - 1];
        const gy = gray[idx + w] - gray[idx - w];
        const magnitude = Math.sqrt((gx * gx) + (gy * gy));
        sum += magnitude;
        sumSq += magnitude * magnitude;
        count += 1;
      }
    }

    if (!count) return 0;
    const mean = sum / count;
    const variance = (sumSq / count) - (mean * mean);
    return Number(Math.max(variance, 0).toFixed(2));
  };

  const generateEditedImageFromSource = useCallback(async ({
    sourceUrl,
    xPercent,
    yPercent,
    widthPercent,
    heightPercent,
    rotation,
  }) => {
    const sourceImage = await loadImageFromUrl(sourceUrl);

    const srcWidth = sourceImage.width;
    const srcHeight = sourceImage.height;

    const cropWidth = Math.max(1, Math.round(srcWidth * (widthPercent / 100)));
    const cropHeight = Math.max(1, Math.round(srcHeight * (heightPercent / 100)));
    const cropX = Math.min(
      Math.max(0, Math.round(srcWidth * (xPercent / 100))),
      Math.max(0, srcWidth - cropWidth)
    );
    const cropY = Math.min(
      Math.max(0, Math.round(srcHeight * (yPercent / 100))),
      Math.max(0, srcHeight - cropHeight)
    );

    const cropCanvas = document.createElement('canvas');
    cropCanvas.width = cropWidth;
    cropCanvas.height = cropHeight;

    const cropCtx = cropCanvas.getContext('2d');
    if (!cropCtx) {
      throw new Error('Failed to process image crop.');
    }

    cropCtx.drawImage(
      sourceImage,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight
    );

    const normalizedRotation = normalizeRotation(rotation);

    const outputCanvas = document.createElement('canvas');
    const rotatedBy90 = normalizedRotation === 90 || normalizedRotation === 270;
    outputCanvas.width = rotatedBy90 ? cropHeight : cropWidth;
    outputCanvas.height = rotatedBy90 ? cropWidth : cropHeight;

    const outCtx = outputCanvas.getContext('2d');
    if (!outCtx) {
      throw new Error('Failed to process image rotation.');
    }

    outCtx.save();
    outCtx.translate(outputCanvas.width / 2, outputCanvas.height / 2);
    outCtx.rotate((normalizedRotation * Math.PI) / 180);
    outCtx.drawImage(cropCanvas, -cropWidth / 2, -cropHeight / 2, cropWidth, cropHeight);
    outCtx.restore();

    const blurScore = calculateBlurScore(outputCanvas);
    const resolutionPass = outputCanvas.width >= MIN_CAPTURE_WIDTH && outputCanvas.height >= MIN_CAPTURE_HEIGHT;
    const warnings = [];
    const errors = [];

    if (!resolutionPass) {
      errors.push(`Resolution too low (${outputCanvas.width}x${outputCanvas.height}). Minimum is ${MIN_CAPTURE_WIDTH}x${MIN_CAPTURE_HEIGHT}.`);
    }
    if (blurScore < BLUR_WARNING_THRESHOLD) {
      warnings.push(`Image may be blurry (sharpness score ${blurScore}). Retake if possible.`);
    }

    const blob = await new Promise((resolve, reject) => {
      outputCanvas.toBlob(
        (resultBlob) => {
          if (!resultBlob) {
            reject(new Error('Failed to generate edited image.'));
            return;
          }
          resolve(resultBlob);
        },
        'image/jpeg',
        0.92
      );
    });

    return {
      blob,
      width: outputCanvas.width,
      height: outputCanvas.height,
      blurScore,
      warnings,
      errors,
      resolutionPass,
    };
  }, []);

  const clearCapturedPhoto = useCallback(() => {
    if (capturedImageSourceUrl) {
      URL.revokeObjectURL(capturedImageSourceUrl);
    }
    if (capturedImagePreviewUrl) {
      URL.revokeObjectURL(capturedImagePreviewUrl);
    }
    setCapturedImageSourceUrl('');
    setCapturedImagePreviewUrl('');
    setCapturedImageFile(null);
    setCapturedImageColorId('');
    setRotationDegrees(0);
    setCropXPercent(0);
    setCropYPercent(0);
    setCropWidthPercent(100);
    setCropHeightPercent(100);
    setCropDragState(null);
    setCaptureCountdown(null);
    setCameraHint('');
    setImageQuality(null);
    if (captureTimerRef.current) {
      clearInterval(captureTimerRef.current);
      captureTimerRef.current = null;
    }
  }, [capturedImageSourceUrl, capturedImagePreviewUrl]);

  useEffect(() => {
    capturedSourceUrlRef.current = capturedImageSourceUrl;
  }, [capturedImageSourceUrl]);

  useEffect(() => {
    capturedPreviewUrlRef.current = capturedImagePreviewUrl;
  }, [capturedImagePreviewUrl]);

  const stopCameraPreview = useCallback(() => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }

    if (cameraVideoRef.current) {
      cameraVideoRef.current.srcObject = null;
    }

    setActiveCameraColorId(null);
    setCaptureCountdown(null);
    if (captureTimerRef.current) {
      clearInterval(captureTimerRef.current);
      captureTimerRef.current = null;
    }
  }, []);

  const startCropDrag = (event, mode) => {
    if (!cropStageRef.current) return;

    event.preventDefault();
    event.stopPropagation();

    setCropDragState({
      mode,
      startX: event.clientX,
      startY: event.clientY,
      initialX: cropXPercent,
      initialY: cropYPercent,
      initialWidth: cropWidthPercent,
      initialHeight: cropHeightPercent,
      stageWidth: cropStageRef.current.clientWidth,
      stageHeight: cropStageRef.current.clientHeight,
    });
  };

  const startCropMove = (event) => startCropDrag(event, 'move');

  const startCropResize = (event) => startCropDrag(event, 'resize');

  const refreshCameraDevices = useCallback(async () => {
    if (!navigator?.mediaDevices?.enumerateDevices) {
      return;
    }
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoInputs = devices.filter((device) => device.kind === 'videoinput');
    setCameraDevices(videoInputs);

    if (videoInputs.length > 0) {
      const hasSelectedDevice = videoInputs.some((device) => device.deviceId === selectedCameraDeviceId);
      if (!selectedCameraDeviceId || !hasSelectedDevice) {
        setSelectedCameraDeviceId(videoInputs[0].deviceId);
      }
    }
  }, [selectedCameraDeviceId]);

  useEffect(() => {
    if (!capturedImageSourceUrl || !capturedImageColorId) return;

    let cancelled = false;

    const processImage = async () => {
      if (cameraBusyRef.current) return;
      cameraBusyRef.current = true;
      setCameraError('');

      try {
        const processed = await generateEditedImageFromSource({
          sourceUrl: capturedImageSourceUrl,
          xPercent: cropXPercent,
          yPercent: cropYPercent,
          widthPercent: cropWidthPercent,
          heightPercent: cropHeightPercent,
          rotation: rotationDegrees,
        });

        if (cancelled) return;

        if (capturedImagePreviewUrl) {
          URL.revokeObjectURL(capturedImagePreviewUrl);
        }

        const timestamp = Date.now();
        const editedFile = new File([processed.blob], `camera-edited-${timestamp}.jpg`, { type: 'image/jpeg' });
        const previewUrl = URL.createObjectURL(processed.blob);
        setCapturedImageFile(editedFile);
        setCapturedImagePreviewUrl(previewUrl);
        setImageQuality({
          width: processed.width,
          height: processed.height,
          blurScore: processed.blurScore,
          warnings: processed.warnings,
          errors: processed.errors,
          resolutionPass: processed.resolutionPass,
        });
      } catch (err) {
        if (!cancelled) {
          setCameraError(err.message || 'Failed to prepare edited image.');
          setCapturedImageFile(null);
          setImageQuality(null);
        }
      } finally {
        cameraBusyRef.current = false;
      }
    };

    processImage();

    return () => {
      cancelled = true;
    };
  }, [
    capturedImageColorId,
    capturedImagePreviewUrl,
    capturedImageSourceUrl,
    cropHeightPercent,
    cropWidthPercent,
    cropXPercent,
    cropYPercent,
    generateEditedImageFromSource,
    rotationDegrees,
  ]);

  useEffect(() => {
    if (!cropDragState) return undefined;

    const handlePointerMove = (event) => {
      event.preventDefault();
      const dx = ((event.clientX - cropDragState.startX) / Math.max(1, cropDragState.stageWidth)) * 100;
      const dy = ((event.clientY - cropDragState.startY) / Math.max(1, cropDragState.stageHeight)) * 100;

      if (cropDragState.mode === 'move') {
        const nextX = clamp(cropDragState.initialX + dx, 0, 100 - cropDragState.initialWidth);
        const nextY = clamp(cropDragState.initialY + dy, 0, 100 - cropDragState.initialHeight);
        setCropXPercent(Number(nextX.toFixed(2)));
        setCropYPercent(Number(nextY.toFixed(2)));
        return;
      }

      const nextWidth = clamp(cropDragState.initialWidth + dx, MIN_CROP_PERCENT, 100 - cropDragState.initialX);
      const nextHeight = clamp(cropDragState.initialHeight + dy, MIN_CROP_PERCENT, 100 - cropDragState.initialY);
      setCropWidthPercent(Number(nextWidth.toFixed(2)));
      setCropHeightPercent(Number(nextHeight.toFixed(2)));
    };

    const handlePointerUp = () => {
      setCropDragState(null);
    };

    window.addEventListener('mousemove', handlePointerMove);
    window.addEventListener('mouseup', handlePointerUp);

    return () => {
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('mouseup', handlePointerUp);
    };
  }, [cropDragState]);

  useEffect(() => {
    return () => {
      stopCameraPreview();
      if (captureTimerRef.current) {
        clearInterval(captureTimerRef.current);
        captureTimerRef.current = null;
      }
      if (capturedSourceUrlRef.current) {
        URL.revokeObjectURL(capturedSourceUrlRef.current);
      }
      if (capturedPreviewUrlRef.current) {
        URL.revokeObjectURL(capturedPreviewUrlRef.current);
      }
    };
  }, [stopCameraPreview]);

  // Initialize Quill when modal opens
  useEffect(() => {
    if (showForm && quillContainerRef.current && !quillInstance.current) {
      const quill = new Quill(quillContainerRef.current, {
        theme: 'snow',
        modules: {
          toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'color': [] }, { 'background': [] }],
            ['link', 'clean']
          ]
        }
      });

      quill.on('text-change', () => {
        const html = quill.root.innerHTML;
        setFormData(prev => ({ ...prev, description: html }));
      });

      quillInstance.current = quill;
    }

    // Cleanup when component unmounts or modal closes
    return () => {
      if (!showForm && quillInstance.current) {
        quillInstance.current = null;
      }
    };
  }, [showForm]);

  // Update Quill content when editing a product
  useEffect(() => {
    if (quillInstance.current && editingProduct) {
      quillInstance.current.root.innerHTML = editingProduct.description || '';
    }
  }, [editingProduct]);

  // Variant management
  const [productVariants, setProductVariants] = useState([]);
  const [showVariantManager, setShowVariantManager] = useState(false);
  const [editingVariant, setEditingVariant] = useState(null);
  const [variantFormData, setVariantFormData] = useState({
    product_version_master_id: '',
    shipment_id: '',
    color_id: '',
    size_id: '',
    stock_price: '',
    stock_quantity: '',
    current_quantity: '',
    current_quantity_update_reason: '',
    variant_sku: '',
    shipping_charge: '',
    value_factor: '',
    discount_percent: '',
    is_featured: false
  });
  const [variantAdjustmentHistory, setVariantAdjustmentHistory] = useState([]);
  const [loadingAdjustmentHistory, setLoadingAdjustmentHistory] = useState(false);

  // Fetch all data on mount
  useEffect(() => {
    fetchAllData();
  }, []);

  // Clear alerts after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  // Fetch subcategories when category changes
  useEffect(() => {
    if (formData.category_id) {
      const category = categories.find(cat => cat.id === formData.category_id);
      if (category && category.subcategories) {
        setSubcategories(category.subcategories);
        setFormData(prev => {
          const currentSubcategoryId = String(prev.subcategory_id || '');
          const isCurrentSubcategoryValid = category.subcategories.some(
            (subcategory) => String(subcategory.id) === currentSubcategoryId
          );

          if (isCurrentSubcategoryValid) {
            return prev;
          }

          return { ...prev, subcategory_id: '' };
        });
      }
    } else {
      setSubcategories([]);
    }
  }, [formData.category_id, categories]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [productsRes, categoriesRes, colorsRes, sizesRes, shipmentsRes, suppliersRes, gstRatesRes] = await Promise.all([
        axios.get('/api/products'),
        axios.get('/api/categories'),
        axios.get('/api/attributes/colors'),
        axios.get('/api/attributes/sizes'),
        axios.get('/api/shipments'),
        axios.get('/api/suppliers'),
        axios.get('/api/gst-rates')
      ]);


      const productsWithVariants = await Promise.all(productsRes.data.map(async (product) => {
        try {
          const [variantRes, colorsRes] = await Promise.all([
            axios.get(`/api/products/${product.id}/with-variants`),
            axios.get(`/api/products/${product.id}/colors-with-urls`)
          ]);
          const enriched = variantRes.data || {};
          const variantTotalStock = (enriched.variants || []).reduce((sum, v) => sum + (v.current_quantity || v.stock_quantity || 0), 0);

          // Find default image thumbnail URL - prioritize default color's default image
          let defaultThumbnailUrl = null;
          if (Array.isArray(colorsRes.data)) {
            // First, try to find the default color
            const defaultColor = colorsRes.data.find(color => color.is_default);
            if (defaultColor && Array.isArray(defaultColor.images)) {
              const defaultImg = defaultColor.images.find(img => img.is_default);
              if (defaultImg && defaultImg.thumbnail_url) {
                defaultThumbnailUrl = defaultImg.thumbnail_url;
              }
            }
            
            // If no default color or no default image in default color, find any default image
            if (!defaultThumbnailUrl) {
              for (const color of colorsRes.data) {
                if (Array.isArray(color.images)) {
                  const defaultImg = color.images.find(img => img.is_default);
                  if (defaultImg && defaultImg.thumbnail_url) {
                    defaultThumbnailUrl = defaultImg.thumbnail_url;
                    break;
                  }
                }
              }
            }
          }

          return {
            ...product,
            has_variants: enriched.has_variants || false,
            variants: enriched.variants || [],
            variant_count: (enriched.variants || []).length,
            variant_total_stock: variantTotalStock,
            default_thumbnail_url: defaultThumbnailUrl
          };
        } catch (err) {
          return {
            ...product,
            has_variants: false,
            variants: [],
            variant_count: 0,
            variant_total_stock: 0,
            default_thumbnail_url: null,
          };
        }
      }));

      setProducts(productsWithVariants);
      setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);
      setColors(Array.isArray(colorsRes.data) ? colorsRes.data : []);
      setSizes(Array.isArray(sizesRes.data) ? sizesRes.data : []);
      setShipments(Array.isArray(shipmentsRes.data) ? shipmentsRes.data : []);
      setSuppliers(Array.isArray(suppliersRes.data) ? suppliersRes.data : []);
      setGstRates(Array.isArray(gstRatesRes.data) ? gstRatesRes.data : []);
    } catch (err) {
      const errorMsg = (err.response?.data?.detail) || err.message || 'Failed to load data';
      console.error('Error fetching data:', err);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductColors = useCallback(async (productId) => {
    try {
      // Use the new endpoint that returns URLs
      const response = await axios.get(`/api/products/${productId}/colors-with-urls`);
      setProductColors(response.data || []);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to fetch colors';
      console.error('Error fetching product colors:', err);
      setError(`Failed to load colors: ${errorMsg}`);
      setProductColors([]);
    }
  }, []);

  const handleAddProduct = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      setError('Product name is required');
      return;
    }
    if (!formData.source_sku.trim()) {
      setError('Source SKU is required');
      return;
    }
    if (!formData.category_id) {
      setError('Category is required');
      return;
    }
    if (!formData.subcategory_id) {
      setError('Subcategory is required');
      return;
    }

    try {
      const payload = { ...formData };

      if (editingProduct) {
        await axios.put(`/api/products/${editingProduct.id}`, payload);
        setSuccess('Product updated successfully');
      } else {
        await axios.post('/api/products', payload);
        setSuccess('Product created successfully');
      }

      resetForm();
      fetchAllData();
    } catch (err) {
      let errorMsg = 'Failed to save product';
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          errorMsg = err.response.data.detail.map(e => `${e.loc[e.loc.length - 1]}: ${e.msg}`).join(', ');
        } else {
          errorMsg = err.response.data.detail;
        }
      } else if (err.message) {
        errorMsg = err.message;
      }
      setError(errorMsg);
      console.error('Error saving product:', err);
    }
  };

  const handleEditProduct = async (product) => {
    try {
      const response = await axios.get(`/api/products/${product.id}`);
      const fullProduct = response.data;
      
      setEditingProduct(fullProduct);
      setFormData({
        name: fullProduct.name,
        description: fullProduct.description || '',
        source_sku: fullProduct.source_sku,
        category_id: fullProduct.category_id,
        subcategory_id: fullProduct.subcategory_id,
        tags: fullProduct.tags || '',
        rate_type: fullProduct.rate_type || '',
        material: fullProduct.material || '',
        return_policy_override: fullProduct.return_policy_override || ''
      });
      setShowForm(true);

      // Load subcategories for the selected category
      const category = categories.find(cat => cat.id === fullProduct.category_id);
      if (category && category.subcategories) {
        setSubcategories(category.subcategories);
      }
    } catch (err) {
      setError('Failed to load product details for editing');
      console.error('Error fetching product for edit:', err);
    }
  };

  const handleDeleteProduct = async (productId) => {
    openConfirmDialog('Delete Product', 'Are you sure you want to delete this product?', 'Delete', async () => {
      try {
        await axios.delete(`/api/products/${productId}`);
        setSuccess('Product deleted successfully');
        fetchAllData();
      } catch (err) {
        const errorMsg = err.response?.data?.detail || 'Failed to delete product';
        setError(errorMsg);
        console.error('Error deleting product:', err);
      }
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      source_sku: '',
      category_id: '',
      subcategory_id: '',
      tags: '',
      rate_type: '',
      material: '',
      return_policy_override: ''
    });
    setEditingProduct(null);
    setShowForm(false);
    setSubcategories([]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Color management handlers
  const handleManageColors = (product) => {
    setSelectedProduct(product);
    setShowColorManager(true);
    setCameraError('');
    stopCameraPreview();
    clearCapturedPhoto();
    // Immediately call fetchProductColors without waiting for state update
    setTimeout(() => {
      fetchProductColors(product.id);
    }, 0);
  };

  const closeColorManager = () => {
    stopCameraPreview();
    clearCapturedPhoto();
    setCameraError('');
    setShowColorManager(false);
    setSelectedProduct(null);
    setProductColors([]);
  };

  const handleAddColor = async () => {
    if (!selectedColor) {
      setError('Please select a color');
      return;
    }

    if (!selectedProduct || !selectedProduct.id) {
      setError('Product not selected. Please close and reopen the modal.');
      return;
    }

    try {
      await axios.post(`/api/products/${selectedProduct.id}/colors`, {
        product_id: selectedProduct.id,
        color_id: selectedColor
      });
      setSuccess('Color added successfully');
      setSelectedColor('');
      
      // Wait a moment for the server to process, then fetch
      setTimeout(() => {
        fetchProductColors(selectedProduct.id);
      }, 200);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to add color';
      console.error('handleAddColor - Error:', err);
      setError(errorMsg);
    }
  };

  const handleRemoveColor = async (colorId) => {
    openConfirmDialog(
      'Remove Color',
      'Are you sure you want to remove this color and all its images?',
      'Remove',
      async () => {
        try {
          await axios.delete(`/api/products/${selectedProduct.id}/colors/${colorId}`);
          setSuccess('Color removed successfully');
          setTimeout(() => {
            fetchProductColors(selectedProduct.id);
          }, 200);
        } catch (err) {
          const errorMsg = err.response?.data?.detail || 'Failed to remove color';
          console.error('handleRemoveColor - Error:', err);
          setError(errorMsg);
        }
      }
    );
  };

  // Image upload handlers
  const getStoredImageSettings = useCallback((productId) => {
    const defaultSettings = {
      width: 2000,
      height: 2000,
      maxSizeMB: 0.5,
      compressionPercent: 80,
    };

    try {
      if (!productId) {
        const rawSession = localStorage.getItem('pugmarkImageSettingsSession');
        return rawSession ? JSON.parse(rawSession) : defaultSettings;
      }

      const key = `pugmarkImageSettings:${productId}`;
      const rawProduct = localStorage.getItem(key);
      if (rawProduct) {
        return JSON.parse(rawProduct);
      }

      const rawSession = localStorage.getItem('pugmarkImageSettingsSession');
      return rawSession ? JSON.parse(rawSession) : defaultSettings;
    } catch {
      return defaultSettings;
    }
  }, []);

  const persistImageSettings = useCallback((settings, productId) => {
    const safeSettings = {
      width: Number(settings.width) || 2000,
      height: Number(settings.height) || 2000,
      maxSizeMB: Number(settings.maxSizeMB) || 0.5,
      compressionPercent: Number(settings.compressionPercent) || 80,
    };

    try {
      localStorage.setItem('pugmarkImageSettingsSession', JSON.stringify(safeSettings));
      if (productId) {
        localStorage.setItem(`pugmarkImageSettings:${productId}`, JSON.stringify(safeSettings));
      }
    } catch {
      // Ignore localStorage errors quietly
    }
  }, []);

  const closeImageCropModal = useCallback(() => {
    setCropModalState({ open: false, productColorId: null, sourceFile: null, previewUrl: '' });
    setProcessedImagePreview((prev) => {
      if (prev?.previewUrl) {
        URL.revokeObjectURL(prev.previewUrl);
      }
      return null;
    });
    setImageCrop({ unit: '%', x: 0, y: 0, width: 100, height: 100, aspect: 1 });
  }, []);

  const openImageCropModal = useCallback((productColorId, file) => {
    if (!file || !file.type.startsWith('image/')) {
      setError('Please choose an image file.');
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    const savedSettings = getStoredImageSettings(selectedProduct?.id || productColorId);
    setImageUploadSettings(savedSettings);
    setCropModalState({
      open: true,
      productColorId,
      sourceFile: file,
      previewUrl,
    });
    setProcessedImagePreview(null);
    setImageCrop({ unit: '%', x: 0, y: 0, width: 100, height: 100, aspect: 1 });
  }, [getStoredImageSettings, selectedProduct?.id]);

  const formatFileSize = useCallback((bytes) => {
    if (!Number.isFinite(bytes) || bytes <= 0) {
      return '0 KB';
    }
    const units = ['B', 'KB', 'MB'];
    let value = bytes;
    let unitIndex = 0;

    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }

    return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
  }, []);

  useEffect(() => {
    if (selectedProduct?.id) {
      const savedSettings = getStoredImageSettings(selectedProduct.id);
      setImageUploadSettings((prev) => ({ width: 2000, height: 2000, maxSizeMB: 0.5, compressionPercent: 80, ...prev, ...savedSettings }));
    }
  }, [getStoredImageSettings, selectedProduct?.id]);

  useEffect(() => {
    if (selectedProduct?.id) {
      persistImageSettings(imageUploadSettings, selectedProduct.id);
    } else {
      persistImageSettings(imageUploadSettings, null);
    }
  }, [imageUploadSettings, persistImageSettings, selectedProduct?.id]);

  const generateProcessedImagePreview = useCallback(async () => {
    if (!cropModalState.sourceFile) {
      return;
    }

    try {
      setError('');
      const prepared = await createTargetImageFile(cropModalState.sourceFile, imageCrop, {
        width: imageUploadSettings.width || TARGET_IMAGE_WIDTH,
        height: imageUploadSettings.height || TARGET_IMAGE_HEIGHT,
        maxSizeMB: imageUploadSettings.maxSizeMB || 0.5,
        compressionPercent: imageUploadSettings.compressionPercent || 80,
      });

      setProcessedImagePreview((prev) => {
        if (prev?.previewUrl) {
          URL.revokeObjectURL(prev.previewUrl);
        }
        return {
          file: prepared.file,
          previewUrl: prepared.previewUrl,
          width: prepared.width,
          height: prepared.height,
          size: prepared.size,
        };
      });
    } catch (err) {
      setError(err?.message || 'Unable to generate final preview.');
    }
  }, [cropModalState.sourceFile, imageCrop, imageUploadSettings]);

  useEffect(() => {
    if (cropModalState.open && cropModalState.sourceFile) {
      generateProcessedImagePreview();
    }
  }, [cropModalState.open, cropModalState.sourceFile, generateProcessedImagePreview]);

  const handleImageUpload = useCallback(async (productColorId, files) => {
    if (!files || files.length === 0) return;

    const uploadPromises = Array.from(files).map(async (file, index) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('display_order', index);

      const response = await axios.post(
        `/api/products/colors/${productColorId}/images`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    });

    await Promise.all(uploadPromises);
    setSuccess('Images uploaded successfully');
    if (selectedProduct?.id) {
      fetchProductColors(selectedProduct.id);
    }
  }, [fetchProductColors, selectedProduct?.id]);

  const handleImageCropApply = useCallback(async () => {
    if (!cropModalState.sourceFile || !cropModalState.productColorId) {
      return;
    }

    setUploadingImages(true);
    setError('');
    try {
      const fileToUpload = processedImagePreview?.file || await createTargetImageFile(cropModalState.sourceFile, imageCrop, {
        width: imageUploadSettings.width || TARGET_IMAGE_WIDTH,
        height: imageUploadSettings.height || TARGET_IMAGE_HEIGHT,
        maxSizeMB: imageUploadSettings.maxSizeMB || 0.5,
        compressionPercent: imageUploadSettings.compressionPercent || 80,
      }).then((result) => result.file);

      await handleImageUpload(cropModalState.productColorId, [fileToUpload]);
      closeImageCropModal();
    } catch (err) {
      const errorMsg = err?.message || 'Failed to prepare image for upload.';
      setError(errorMsg);
    } finally {
      setUploadingImages(false);
    }
  }, [closeImageCropModal, cropModalState.productColorId, cropModalState.sourceFile, handleImageUpload, imageCrop, imageUploadSettings, processedImagePreview]);

  const handleImageSelection = (productColorId, files) => {
    if (!files || files.length === 0) return;

    const selectedFile = files[0];
    openImageCropModal(productColorId, selectedFile);
  };

  const handleStartCamera = async (productColorId) => {
    setCameraError('');
    setCameraHint('Tip: hold the product steady and use good light for best focus.');

    if (!navigator?.mediaDevices?.getUserMedia) {
      setCameraError('Camera access is not supported in this browser.');
      return;
    }

    if (capturedImageColorId === productColorId) {
      clearCapturedPhoto();
    }

    stopCameraPreview();

    try {
      await refreshCameraDevices();

      const videoConstraints = selectedCameraDeviceId
        ? { deviceId: { exact: selectedCameraDeviceId } }
        : { facingMode: { ideal: 'environment' } };

      const stream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints,
        audio: false,
      });

      const videoTrack = stream.getVideoTracks()[0];
      const capabilities = videoTrack?.getCapabilities?.() || {};
      if (capabilities.focusMode && capabilities.focusMode.includes('continuous')) {
        try {
          await videoTrack.applyConstraints({ advanced: [{ focusMode: 'continuous' }] });
          setCameraHint('Auto-focus enabled. Hold steady for 3 seconds before capture.');
        } catch {
          setCameraHint('Tip: move camera slightly to help focus, then hold steady.');
        }
      }

      cameraStreamRef.current = stream;
      setActiveCameraColorId(productColorId);
      await refreshCameraDevices();

      setTimeout(() => {
        if (cameraVideoRef.current) {
          cameraVideoRef.current.srcObject = stream;
          cameraVideoRef.current.play().catch(() => {
            setCameraError('Unable to start camera preview. Please allow camera access.');
          });
        }
      }, 0);
    } catch (err) {
      const message = err?.name === 'NotAllowedError'
        ? 'Camera permission denied. Please allow camera access and try again.'
        : err?.name === 'OverconstrainedError'
          ? 'Selected camera is unavailable. Choose a different camera and retry.'
        : 'Unable to access camera. Please check camera connection and permissions.';
      setCameraError(message);
    }
  };

  const captureSnapshotNow = (productColorId) => {
    const video = cameraVideoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) {
      setCameraError('Camera is not ready yet. Please wait and try again.');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext('2d');
    if (!context) {
      setCameraError('Failed to capture image from camera.');
      return;
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setCameraError('Failed to capture image. Please try again.');
          return;
        }

        if (capturedImagePreviewUrl) {
          URL.revokeObjectURL(capturedImagePreviewUrl);
        }

        const sourceUrl = URL.createObjectURL(blob);

        if (capturedImageSourceUrl) {
          URL.revokeObjectURL(capturedImageSourceUrl);
        }

        setCapturedImageSourceUrl(sourceUrl);
        setCapturedImageColorId(productColorId);
        setRotationDegrees(0);
        setCropXPercent(0);
        setCropYPercent(0);
        setCropWidthPercent(100);
        setCropHeightPercent(100);
        setCameraHint('Review, crop, rotate, then upload.');
        stopCameraPreview();
      },
      'image/jpeg',
      0.92
    );
  };

  const handleCaptureSnapshot = (productColorId) => {
    if (captureCountdown !== null) {
      return;
    }

    setCameraError('');
    setCameraHint('Hold steady... capturing in 3');
    setCaptureCountdown(3);

    let remaining = 3;
    captureTimerRef.current = setInterval(() => {
      remaining -= 1;

      if (remaining > 0) {
        setCaptureCountdown(remaining);
        setCameraHint(`Hold steady... capturing in ${remaining}`);
        return;
      }

      if (captureTimerRef.current) {
        clearInterval(captureTimerRef.current);
        captureTimerRef.current = null;
      }
      setCaptureCountdown(null);
      captureSnapshotNow(productColorId);
    }, 1000);
  };

  const handleUploadCapturedPhoto = async (productColorId) => {
    if (!capturedImageFile || capturedImageColorId !== productColorId) {
      setCameraError('Capture an image first before uploading.');
      return;
    }

    if (imageQuality?.errors?.length) {
      setCameraError('Fix image quality issues before uploading.');
      return;
    }

    if (strictBlurMode && imageQuality?.warnings?.length) {
      setCameraError('Strict blur mode is enabled. Retake or improve focus before upload.');
      return;
    }

    await handleImageUpload(productColorId, [capturedImageFile]);
    clearCapturedPhoto();
  };

  const handleRetakePhoto = (productColorId) => {
    clearCapturedPhoto();
    handleStartCamera(productColorId);
  };

  const handleRotateLeft = () => {
    setRotationDegrees((prev) => normalizeRotation(prev - 90));
  };

  const handleRotateRight = () => {
    setRotationDegrees((prev) => normalizeRotation(prev + 90));
  };

  const handleCameraDeviceChange = async (productColorId, deviceId) => {
    setSelectedCameraDeviceId(deviceId);
    if (activeCameraColorId === productColorId) {
      await handleStartCamera(productColorId);
    }
  };

  const handleDeleteImage = async (imageId) => {
    openConfirmDialog('Delete Image', 'Are you sure you want to delete this image?', 'Delete', async () => {
      try {
        await axios.delete(`/api/products/images/${imageId}`);
        setSuccess('Image deleted successfully');
        fetchProductColors(selectedProduct.id);
      } catch (err) {
        const errorMsg = err.response?.data?.detail || 'Failed to delete image';
        setError(errorMsg);
      }
    });
  };

  // Default color and image handlers
  const handleSetDefaultColor = async (colorId) => {
    try {
      await axios.put(`/api/products/${selectedProduct.id}/colors/${colorId}/set-default`);
      setSuccess('Default color set successfully');
      fetchProductColors(selectedProduct.id);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to set default color';
      setError(errorMsg);
    }
  };

  const handleSetDefaultImage = async (imageId) => {
    try {
      await axios.put(`/api/products/images/${imageId}/set-default`);
      setSuccess('Default image set successfully');
      fetchProductColors(selectedProduct.id);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to set default image';
      setError(errorMsg);
    }
  };

  // Variant management handlers
  const fetchProductVariants = async (productId) => {
    try {
      const response = await axios.get(`/api/products/${productId}/variants`);
      setProductVariants(response.data);
    } catch (err) {
      console.error('Error fetching product variants:', err);
      setProductVariants([]);
    }
  };

  const handleManageVariants = (product) => {
    setSelectedProduct(product);
    setShowVariantManager(true);
    setVariantError('');
    fetchProductVariants(product.id);
    fetchProductColors(product.id);
  };

  const resetVariantForm = () => {
    setVariantFormData({
      product_version_master_id: '',
      shipment_id: '',
      color_id: '',
      size_id: '',
      stock_price: '',
      stock_quantity: '',
      current_quantity: '',
      current_quantity_update_reason: '',
      variant_sku: '',
      shipping_charge: '',
      value_factor: '',
      discount_percent: '',
      is_featured: false
    });
    setEditingVariant(null);
    setVariantError('');
    setVariantAdjustmentHistory([]);
    setLoadingAdjustmentHistory(false);
  };

  const fetchVariantAdjustmentHistory = async (variantId) => {
    if (!variantId) {
      setVariantAdjustmentHistory([]);
      return;
    }

    try {
      setLoadingAdjustmentHistory(true);
      const response = await axios.get(`/api/products/variants/${variantId}/inventory-adjustments`);
      setVariantAdjustmentHistory(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setVariantAdjustmentHistory([]);
    } finally {
      setLoadingAdjustmentHistory(false);
    }
  };

  const handleVariantInputChange = (e) => {
    const { name, value } = e.target;
    setVariantFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const generateVariantSKU = useCallback(() => {
    const baseSku = (selectedProduct?.source_sku || selectedProduct?.name || 'SKU').toString().trim().toUpperCase();
    const colorName = colors.find(c => c.id === variantFormData.color_id)?.name || '';
    const sizeName = sizes.find(s => s.id === variantFormData.size_id)?.name || '';
    const parts = [baseSku];
    if (colorName) parts.push(`C-${colorName.replace(/\s+/g, '').toUpperCase()}`);
    if (sizeName) parts.push(`S-${sizeName.replace(/\s+/g, '').toUpperCase()}`);
    return parts.join('-');
  }, [selectedProduct, variantFormData.color_id, variantFormData.size_id, colors, sizes]);

  const [suggestedVariantSKU, setSuggestedVariantSKU] = useState('');

  React.useEffect(() => {
    setSuggestedVariantSKU(generateVariantSKU());
  }, [generateVariantSKU]);

  const copySKUToClipboard = () => {
    const sku = variantFormData.variant_sku?.trim() || suggestedVariantSKU;
    navigator.clipboard.writeText(sku).then(() => {
      setSuccess('Variant SKU copied to clipboard');
    }).catch(() => {
      setVariantError('Failed to copy SKU');
    });
  };

  const handleAddVariant = async (e) => {
    e.preventDefault();

    // Validation
    if (!variantFormData.color_id && !variantFormData.size_id) {
      setVariantError('At least one of color or size must be selected');
      return;
    }
    if (!variantFormData.stock_quantity) {
      setVariantError('Stock quantity is required');
      return;
    }
    if (!variantFormData.shipment_id) {
      setVariantError('Shipment is required');
      return;
    }

    // Dedup detection
    const duplicateFound = productVariants.some(v =>
      v.is_active &&
      (v.color_id || '') === (variantFormData.color_id || '') &&
      (v.size_id || '') === (variantFormData.size_id || '') &&
      String(v.shipment_id || '') === String(variantFormData.shipment_id || '')
    );

    if (duplicateFound && !editingVariant) {
      setVariantError('A variant version with selected color/size already exists for this shipment.');
      return;
    }

    try {
      const variantSku = variantFormData.variant_sku?.trim() || generateVariantSKU();
      const parsedStockQuantity = parseInt(variantFormData.stock_quantity, 10);
      const parsedCurrentQuantity = variantFormData.current_quantity !== ''
        ? parseInt(variantFormData.current_quantity, 10)
        : null;
      const payload = {
        product_version_master_id: variantFormData.product_version_master_id || null,
        shipment_id: variantFormData.shipment_id ? parseInt(variantFormData.shipment_id, 10) : null,
        color_id: variantFormData.color_id || null,
        size_id: variantFormData.size_id || null,
        stock_price: variantFormData.stock_price ? parseFloat(variantFormData.stock_price) : null,
        stock_quantity: parsedStockQuantity,
        variant_sku: variantSku,
        shipping_charge: variantFormData.shipping_charge ? parseFloat(variantFormData.shipping_charge) : null,
        value_factor: variantFormData.value_factor ? parseFloat(variantFormData.value_factor) : null,
        discount_percent: variantFormData.discount_percent ? parseFloat(variantFormData.discount_percent) : null
      };

      if (editingVariant) {
        payload.is_featured = Boolean(variantFormData.is_featured);
        const originalCurrentQuantity = parseInt(
          editingVariant.current_quantity ?? editingVariant.stock_quantity ?? 0,
          10
        );
        const newCurrentQuantity = parsedCurrentQuantity;
        if (newCurrentQuantity !== null) {
          payload.current_quantity = newCurrentQuantity;
          if (newCurrentQuantity !== originalCurrentQuantity) {
            const reason = (variantFormData.current_quantity_update_reason || '').trim();
            if (!reason) {
              setVariantError('Reason is required when current quantity is changed');
              return;
            }
            payload.current_quantity_update_reason = reason;
          }
        }
      }

      if (editingVariant) {
        await axios.put(`/api/products/variants/${editingVariant.id}`, payload);
        setSuccess('Variant updated successfully');
      } else {
        // FIX: Use correct endpoint for creating a variant (needs product_id in path)
        await axios.post(`/api/products/${selectedProduct.id}/variants`, payload);
        setSuccess('Variant created successfully');
      }

      resetVariantForm();
      fetchProductVariants(selectedProduct.id);
    } catch (err) {
      let errorMsg = 'Failed to save variant';
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          errorMsg = err.response.data.detail.map(e => `${e.loc[e.loc.length - 1]}: ${e.msg}`).join(', ');
        } else {
          errorMsg = err.response.data.detail;
        }
      }
      setVariantError(errorMsg);
    }
  };

  const handleEditVariant = (variant) => {
    setEditingVariant(variant);
    setVariantFormData({
      product_version_master_id: variant.product_version_master_id || '',
      shipment_id: variant.shipment_id || '',
      color_id: variant.color_id || '',
      size_id: variant.size_id || '',
      stock_price: variant.stock_price || '',
      stock_quantity: variant.stock_quantity,
      current_quantity: variant.current_quantity ?? variant.stock_quantity ?? 0,
      current_quantity_update_reason: '',
      variant_sku: variant.variant_sku || '',
      shipping_charge: variant.shipping_charge || '',
      value_factor: variant.value_factor || '',
      discount_percent: variant.discount_percent || '',
      is_featured: Boolean(variant.is_featured)
    });
    fetchVariantAdjustmentHistory(variant.id);
  };

  const handleToggleStatus = async (product) => {
    try {
      await axios.put(`/api/products/${product.id}`, {
        is_active: !product.is_active
      });
      setSuccess(`Product ${product.is_active ? 'deactivated' : 'activated'} successfully`);
      fetchAllData();
    } catch (err) {
      setError('Failed to update status');
    }
  };

  const handleDeleteVariant = async (variantId) => {
    openConfirmDialog('Delete Variant', 'Are you sure you want to delete this variant?', 'Delete', async () => {
      try {
        await axios.delete(`/api/products/variants/${variantId}`);
        setSuccess('Variant deleted successfully');
        fetchProductVariants(selectedProduct.id);
      } catch (err) {
        const errorMsg = err.response?.data?.detail || 'Failed to delete variant';
        setVariantError(errorMsg);
      }
    });
  };

  const handleBulkCreateVariants = async () => {
    if (!variantFormData.shipment_id) {
      setVariantError('Shipment is required for bulk variant creation');
      return;
    }

    openConfirmDialog(
      'Create Variant Combinations',
      'This will create variants for all combinations of colors and sizes. Continue?',
      'Create Variants',
      async () => {
        try {
          await axios.post(`/api/products/${selectedProduct.id}/variants/bulk`, null, {
            params: {
              shipment_id: parseInt(variantFormData.shipment_id, 10),
            },
          });
          setSuccess('Variants created successfully');
          fetchProductVariants(selectedProduct.id);
        } catch (err) {
          let errorMsg = 'Failed to create variants';
          if (err.response?.data?.detail) {
            if (Array.isArray(err.response.data.detail)) {
              errorMsg = err.response.data.detail.map(e => `${e.loc[e.loc.length - 1]}: ${e.msg}`).join(', ');
            } else {
              errorMsg = err.response.data.detail;
            }
          }
          setVariantError(errorMsg);
        }
      }
    );
  };

  const openConfirmDialog = (title, message, confirmText, onConfirm) => {
    setConfirmDialog({
      show: true,
      title,
      message,
      confirmText,
      onConfirm,
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ show: false, title: '', message: '', confirmText: 'Confirm', onConfirm: null });
  };

  const executeConfirmDialogAction = async () => {
    const action = confirmDialog.onConfirm;
    closeConfirmDialog();
    if (typeof action === 'function') {
      await action();
    }
  };

  const confirmActionClass = /delete|cancel|remove|unpublish/i.test(confirmDialog.confirmText || '')
    ? 'btn btn-danger'
    : 'btn btn-primary';

  return (
    <div className="product-manager-page">
      {/* Alerts */}
      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Add Button */}
      <div style={{ marginBottom: '20px' }}>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          + Add Product
        </button>
      </div>

      {/* Product Form Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content large-modal">
            <div className="modal-header">
              <h2>{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
              <button
                className="btn btn-sm btn-secondary"
                onClick={resetForm}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <form className="product-form" onSubmit={handleAddProduct}>
                <div className="form-group">
                  <label>Product Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter product name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <div style={{ backgroundColor: 'white', minHeight: '150px' }}>
                    <div ref={quillContainerRef} style={{ height: '200px' }} />
                  </div>
                </div>

                <div className="form-group">
                  <label>Source SKU *</label>
                  <input
                    type="text"
                    name="source_sku"
                    value={formData.source_sku}
                    onChange={handleInputChange}
                    placeholder="Enter source SKU"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>GST Rate Type</label>
                  <select
                    name="rate_type"
                    value={formData.rate_type}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Rate Type</option>
                    {Array.from(new Set(gstRates.map(rate => rate.rate_type))).map(rateType => (
                      <option key={rateType} value={rateType}>
                        {rateType}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Category *</label>
                    <select
                      name="category_id"
                      value={formData.category_id}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Category</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Subcategory *</label>
                    <select
                      name="subcategory_id"
                      value={formData.subcategory_id}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Subcategory</option>
                      {subcategories.map(subcat => (
                        <option key={subcat.id} value={subcat.id}>
                          {subcat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Tags</label>
                  <input
                    type="text"
                    name="tags"
                    value={formData.tags}
                    onChange={handleInputChange}
                    placeholder="e.g., bestseller, new, sale"
                    maxLength="40"
                  />
                </div>

                <div className="form-group">
                  <label>Material</label>
                  <input
                    type="text"
                    name="material"
                    value={formData.material}
                    onChange={handleInputChange}
                    placeholder="e.g., Cotton, Polyester, Leather"
                    maxLength="100"
                  />
                </div>

                <div className="form-group">
                  <label>Return Policy Override</label>
                  <select
                    name="return_policy_override"
                    value={formData.return_policy_override}
                    onChange={handleInputChange}
                  >
                    <option value="">Use Store Default</option>
                    <option value="non-returnable">Non-Returnable</option>
                    <option value="5-days">5 Days</option>
                    <option value="10-days">10 Days</option>
                    <option value="15-days">15 Days</option>
                    <option value="30-days">30 Days</option>
                  </select>
                  <small className="text-muted">Leave as "Use Store Default" to inherit the store-level return policy.</small>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-success">
                    {editingProduct ? 'Update Product' : 'Create Product'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={resetForm}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Products Table */}
      {products.length > 0 && (
        <div className="products-table">
          <h2>Products ({products.length})</h2>

          {/* Filters and Search Bar */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: 16, flexWrap: 'wrap', backgroundColor: '#f9f9f9', padding: '12px', borderRadius: '8px', border: '1px solid #eee' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontWeight: 'bold' }}>Category: </label>
              <select 
                value={filterCategory} 
                onChange={e => {
                  setFilterCategory(e.target.value);
                  setFilterSubcategory('');
                }}
                style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontWeight: 'bold' }}>Subcategory: </label>
              <select 
                value={filterSubcategory} 
                onChange={e => setFilterSubcategory(e.target.value)}
                style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="">All Subcategories</option>
                {filterCategory && categories.find(cat => cat.id === filterCategory)?.subcategories?.map(sub => (
                  <option key={sub.id} value={sub.id}>{sub.name}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontWeight: 'bold' }}>SKU: </label>
              <input
                type="text"
                value={filterSKU}
                onChange={e => setFilterSKU(e.target.value)}
                placeholder="Filter by SKU"
                style={{ width: 120, padding: '4px 8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
            </div>

            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontWeight: 'bold' }}>Search: </label>
              <input
                type="text"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder="Search by product name..."
                style={{ width: '100%', padding: '4px 8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Product Name</th>
                <th>SKU</th>
                <th>GST Rate Type</th>
                <th>Category</th>
                <th>Status</th>
                <th>Sold Out</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map(product => (
                <tr key={product.id}>
                  <td style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {product.default_thumbnail_url && (
                      <img
                        src={product.default_thumbnail_url}
                        alt="thumbnail"
                        style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, border: '1px solid #ccc' }}
                      />
                    )}
                    {product.name}
                  </td>
                  <td>{product.source_sku}</td>
                  <td>{product.rate_type || '-'}</td>
                  <td>{
                    (() => {
                      const cat = categories.find(c => c.id === product.category_id);
                      return cat ? cat.name : product.category_id;
                    })()
                  }</td>
                  <td>
                    <span 
                      className={`status ${product.is_active ? 'active' : 'inactive'}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleToggleStatus(product)}
                      title="Click to toggle status"
                    >
                      {product.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    {product.sold_out ? (
                      <span className="badge badge-danger" style={{ backgroundColor: '#f56565', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '0.8rem' }}>Sold Out</span>
                    ) : (
                      <span className="badge badge-success" style={{ backgroundColor: '#48bb78', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '0.8rem' }}>In Stock</span>
                    )}
                  </td>
                  <td className="actions">
                    <button
                      className="btn btn-sm btn-info"
                      onClick={() => handleEditProduct(product)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => handleManageColors(product)}
                    >
                      Colors
                    </button>
                    <button
                      className="btn btn-sm btn-warning"
                      onClick={() => handleManageVariants(product)}
                    >
                      Variants
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteProduct(product.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Color Manager Modal */}
      {showColorManager && selectedProduct && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Manage Colors for {selectedProduct.name}</h2>
              <button
                className="btn btn-sm btn-secondary"
                onClick={closeColorManager}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              {/* Add Color Section */}
              <div className="add-color-section">
                <h3>Add Color Option</h3>
                <div className="form-row">
                  <select
                    value={selectedColor}
                    onChange={(e) => setSelectedColor(e.target.value)}
                  >
                    <option value="">Select Color</option>
                    {colors
                      .filter(color => !productColors.some(pc => pc.color_id === color.id))
                      .map(color => (
                        <option key={color.id} value={color.id}>
                          {color.name}
                        </option>
                      ))}
                  </select>
                  <button
                    className="btn btn-success"
                    onClick={handleAddColor}
                    disabled={!selectedColor}
                  >
                    Add Color
                  </button>
                </div>
              </div>

              {/* Colors List */}
              <div style={{ marginTop: '20px', border: '1px solid #ddd', padding: '10px', borderRadius: '4px' }}>
                {productColors && Array.isArray(productColors) ? (
                  <div>
                    <p style={{ color: '#666', fontSize: '0.9rem' }}>
                      <strong>Debug:</strong> Found {productColors.length} color(s)
                    </p>
                    {productColors.length > 0 && (
                      <div className="colors-list">
                        <h3>Product Colors ({productColors.length})</h3>
                        {productColors.map((productColor, index) => (
                          <div key={productColor.id} className="color-item">
                            <div className="color-header">
                              <h4>
                                {productColor.color?.name || 'Unknown Color'}
                                {productColor.is_default && <span className="default-badge"> (Default)</span>}
                              </h4>
                              <div className="color-actions">
                                {!productColor.is_default && (
                                  <button
                                    className="btn btn-sm btn-primary"
                                    onClick={() => handleSetDefaultColor(productColor.color_id)}
                                  >
                                    Set as Default
                                  </button>
                                )}
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleRemoveColor(productColor.color_id)}
                                >
                                  Remove Color
                                </button>
                              </div>
                            </div>

                            {/* Images Section */}
                            <div className="images-section">
                              <h5>Images ({productColor.images.length})</h5>

                              {/* Image Upload */}
                              <div className="image-upload">
                                <input
                                  type="file"
                                  multiple
                                  accept="image/*"
                                  onChange={(e) => handleImageSelection(productColor.id, e.target.files)}
                                  disabled={uploadingImages}
                                />
                                {uploadingImages && <span>Uploading...</span>}

                                {cropModalState.open && cropModalState.productColorId === productColor.id && (
                                  <div className="crop-modal-backdrop">
                                    <div className="crop-modal">
                                      <div className="crop-modal-header">
                                        <h4>Crop and compress image</h4>
                                        <button type="button" className="btn btn-sm btn-secondary" onClick={closeImageCropModal}>Close</button>
                                      </div>

                                      <div className="crop-modal-body">
                                        <div className="upload-settings-grid">
                                          <label>
                                            Width (px)
                                            <input
                                              type="number"
                                              min="200"
                                              max="5000"
                                              value={imageUploadSettings.width}
                                              onChange={(e) => {
                                                const nextValue = Number(e.target.value) || 2000;
                                                setImageUploadSettings((prev) => ({ ...prev, width: nextValue }));
                                              }}
                                            />
                                          </label>
                                          <label>
                                            Height (px)
                                            <input
                                              type="number"
                                              min="200"
                                              max="5000"
                                              value={imageUploadSettings.height}
                                              onChange={(e) => {
                                                const nextValue = Number(e.target.value) || 2000;
                                                setImageUploadSettings((prev) => ({ ...prev, height: nextValue }));
                                              }}
                                            />
                                          </label>
                                          <label>
                                            Max file size (MB)
                                            <input
                                              type="number"
                                              min="0.5"
                                              max="2"
                                              step="0.1"
                                              value={imageUploadSettings.maxSizeMB}
                                              onChange={(e) => {
                                                const nextValue = Number(e.target.value) || 0.5;
                                                setImageUploadSettings((prev) => ({ ...prev, maxSizeMB: nextValue }));
                                              }}
                                            />
                                          </label>
                                          <label>
                                            Compression % (optional max)
                                            <input
                                              type="number"
                                              min="10"
                                              max="100"
                                              step="5"
                                              value={imageUploadSettings.compressionPercent}
                                              onChange={(e) => {
                                                const raw = e.target.value;
                                                setImageUploadSettings((prev) => ({
                                                  ...prev,
                                                  compressionPercent: raw === '' ? null : Number(raw),
                                                }));
                                              }}
                                            />
                                          </label>
                                        </div>

                                        <ReactCrop
                                          crop={imageCrop}
                                          onChange={setImageCrop}
                                          minWidth={200}
                                          minHeight={200}
                                          aspect={imageUploadSettings.width / imageUploadSettings.height || 1}
                                          keepSelection
                                        >
                                          <img src={cropModalState.previewUrl} alt="Crop preview" style={{ maxHeight: 420, maxWidth: '100%' }} />
                                        </ReactCrop>

                                        <div className="quality-summary">
                                          <p>
                                            Target size: <strong>{imageUploadSettings.width}x{imageUploadSettings.height}px</strong>
                                          </p>
                                          <p>
                                            Final size target: under <strong>{imageUploadSettings.maxSizeMB} MB</strong>
                                            {imageUploadSettings.compressionPercent !== null && imageUploadSettings.compressionPercent !== undefined ? ` with a maximum compression of <strong>${imageUploadSettings.compressionPercent}%</strong>.` : '.'}
                                          </p>
                                        </div>

                                        {processedImagePreview && (
                                          <div className="final-preview-panel">
                                            <div className="final-preview-header">
                                              <h5>Final preview before upload</h5>
                                              <span>{formatFileSize(processedImagePreview.size)}</span>
                                            </div>
                                            <img
                                              src={processedImagePreview.previewUrl}
                                              alt="Final processed preview"
                                              className="final-preview-image"
                                            />
                                            <p className="final-preview-meta">
                                              {processedImagePreview.width} × {processedImagePreview.height}px • {formatFileSize(processedImagePreview.size)}
                                            </p>
                                          </div>
                                        )}
                                      </div>

                                      <div className="camera-capture-actions">
                                        <button type="button" className="btn btn-sm btn-success" onClick={handleImageCropApply} disabled={uploadingImages || !processedImagePreview}>
                                          Upload Cropped Image
                                        </button>
                                        <button type="button" className="btn btn-sm btn-secondary" onClick={closeImageCropModal} disabled={uploadingImages}>
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                <div className="camera-capture-panel">
                                  <div className="camera-device-row">
                                    <label htmlFor={`camera-device-${productColor.id}`}>Camera</label>
                                    <select
                                      id={`camera-device-${productColor.id}`}
                                      value={selectedCameraDeviceId}
                                      onChange={(e) => handleCameraDeviceChange(productColor.id, e.target.value)}
                                      disabled={uploadingImages}
                                    >
                                      {cameraDevices.length === 0 && <option value="">Default camera</option>}
                                      {cameraDevices.map((device, index) => (
                                        <option key={device.deviceId || `camera-${index}`} value={device.deviceId}>
                                          {device.label || `Camera ${index + 1}`}
                                        </option>
                                      ))}
                                    </select>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-secondary"
                                      onClick={refreshCameraDevices}
                                      disabled={uploadingImages}
                                    >
                                      Refresh
                                    </button>
                                  </div>

                                  <div className="camera-capture-actions">
                                    {activeCameraColorId === productColor.id ? (
                                      <>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-warning"
                                          onClick={stopCameraPreview}
                                          disabled={uploadingImages}
                                        >
                                          Stop Camera
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-primary"
                                          onClick={() => handleCaptureSnapshot(productColor.id)}
                                          disabled={uploadingImages || captureCountdown !== null}
                                        >
                                          {captureCountdown !== null ? `Capturing in ${captureCountdown}` : 'Capture Photo'}
                                        </button>
                                      </>
                                    ) : (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-info"
                                        onClick={() => handleStartCamera(productColor.id)}
                                        disabled={uploadingImages}
                                      >
                                        Open Camera
                                      </button>
                                    )}
                                  </div>

                                  {activeCameraColorId === productColor.id && (
                                    <div className="camera-preview-wrap">
                                      <video
                                        ref={cameraVideoRef}
                                        className="camera-preview"
                                        autoPlay
                                        muted
                                        playsInline
                                      />
                                    </div>
                                  )}

                                  {capturedImageColorId === productColor.id && capturedImagePreviewUrl && (
                                    <div className="captured-preview-wrap">
                                      <div className="camera-editor-controls">
                                        <div className="camera-capture-actions">
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-secondary"
                                            onClick={handleRotateLeft}
                                            disabled={uploadingImages}
                                          >
                                            Rotate Left
                                          </button>
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-secondary"
                                            onClick={handleRotateRight}
                                            disabled={uploadingImages}
                                          >
                                            Rotate Right
                                          </button>
                                        </div>

                                        <p className="crop-help-text">
                                          Drag the crop box to move. Drag the bottom-right handle to resize.
                                        </p>

                                        <div ref={cropStageRef} className="crop-stage">
                                          <img
                                            src={capturedImageSourceUrl || capturedImagePreviewUrl}
                                            alt="Crop source"
                                            className="crop-source-image"
                                          />
                                          <div
                                            className="crop-rect"
                                            style={{
                                              left: `${cropXPercent}%`,
                                              top: `${cropYPercent}%`,
                                              width: `${cropWidthPercent}%`,
                                              height: `${cropHeightPercent}%`,
                                            }}
                                            onMouseDown={startCropMove}
                                          >
                                            <span className="crop-rect-label">Crop</span>
                                            <button
                                              type="button"
                                              className="crop-resize-handle"
                                              onMouseDown={startCropResize}
                                              aria-label="Resize crop"
                                            />
                                          </div>
                                        </div>
                                      </div>

                                      <img
                                        src={capturedImagePreviewUrl}
                                        alt="Captured product preview"
                                        className="captured-preview-image"
                                      />

                                      {imageQuality && (
                                        <div className="quality-summary">
                                          <p>
                                            Output: <strong>{imageQuality.width}x{imageQuality.height}</strong> | Sharpness score: <strong>{imageQuality.blurScore}</strong>
                                          </p>
                                          <label className="strict-blur-toggle">
                                            <input
                                              type="checkbox"
                                              checked={strictBlurMode}
                                              onChange={(e) => setStrictBlurMode(e.target.checked)}
                                            />
                                            Strict blur mode (block upload on blur warning)
                                          </label>
                                          {imageQuality.errors.map((item) => (
                                            <p key={`err-${item}`} className="quality-error">{item}</p>
                                          ))}
                                          {imageQuality.warnings.map((item) => (
                                            <p key={`warn-${item}`} className="quality-warning">{item}</p>
                                          ))}
                                        </div>
                                      )}

                                      <div className="camera-capture-actions">
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-success"
                                          onClick={() => handleUploadCapturedPhoto(productColor.id)}
                                          disabled={uploadingImages || !capturedImageFile || Boolean(imageQuality?.errors?.length) || (strictBlurMode && Boolean(imageQuality?.warnings?.length))}
                                        >
                                          Upload This Photo
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-secondary"
                                          onClick={() => handleRetakePhoto(productColor.id)}
                                          disabled={uploadingImages}
                                        >
                                          Retake
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-danger"
                                          onClick={clearCapturedPhoto}
                                          disabled={uploadingImages}
                                        >
                                          Discard
                                        </button>
                                      </div>
                                    </div>
                                  )}

                                  {cameraError && (activeCameraColorId === productColor.id || capturedImageColorId === productColor.id) && (
                                    <p className="camera-error-text">{cameraError}</p>
                                  )}
                                  {cameraHint && (activeCameraColorId === productColor.id || capturedImageColorId === productColor.id) && (
                                    <p className="camera-hint-text">{cameraHint}</p>
                                  )}
                                </div>
                              </div>

                              {/* Images Grid */}
                              {productColor.images.length > 0 && (
                                <div className="images-grid">
                                  {productColor.images
                                    .sort((a, b) => a.display_order - b.display_order)
                                    .map(image => (
                                      <div key={image.id} className={`image-item ${image.is_default ? 'default-image' : ''}`}>
                                        {image.is_default && <span className="default-image-badge">Default</span>}
                                        <img
                                          src={image.thumbnail_url}
                                          alt={image.image_name}
                                          onClick={() => window.open(image.large_url, '_blank')}
                                        />
                                        <div className="image-actions">
                                          {!image.is_default && (
                                            <button
                                              className="btn btn-xs btn-primary"
                                              onClick={() => handleSetDefaultImage(image.id)}
                                            >
                                              Set Default
                                            </button>
                                          )}
                                          <button
                                            className="btn btn-xs btn-danger"
                                            onClick={() => handleDeleteImage(image.id)}
                                          >
                                            Delete
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <p style={{ color: '#999' }}>Loading colors...</p>
                )}
              </div>

              {/* No colors message */}
              {(!productColors || productColors.length === 0) && (
                <div style={{
                  padding: '20px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  textAlign: 'center',
                  color: '#666'
                }}>
                  <p>No colors added yet. Select a color above to add one.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Variant Manager Modal */}
      {showVariantManager && selectedProduct && (
        <div className="modal-overlay">
          <div className="modal-content large-modal">
            <div className="modal-header">
              <h2>Manage Variants for {selectedProduct.name}</h2>
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => {
                  setShowVariantManager(false);
                  setSelectedProduct(null);
                  setProductVariants([]);
                  resetVariantForm();
                }}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              {variantError && <div className="alert alert-error">{variantError}</div>}
              {/* Bulk Create Variants */}
              <div className="bulk-actions">
                <button
                  className="btn btn-primary"
                  onClick={handleBulkCreateVariants}
                >
                  Create All Color/Size Combinations
                </button>
                <p className="help-text">
                  This will create variants for all combinations of colors and sizes associated with this product.
                </p>
              </div>

              {/* Add/Edit Variant Form */}
              <div className="variant-form-section">
                <h3>{editingVariant ? 'Edit Variant' : 'Add New Variant'}</h3>
                <form onSubmit={handleAddVariant}>
                  <div className="form-group">
                    <label>Shipment *</label>
                    <select
                      name="shipment_id"
                      value={variantFormData.shipment_id}
                      onChange={handleVariantInputChange}
                      required
                    >
                      <option value="">Select Shipment</option>
                      {shipments.map((shipment) => (
                        <option key={shipment.id} value={shipment.id}>
                          {shipment.shipment_id} ({suppliers.find((sup) => sup.id === shipment.supplier_id)?.supplier_name || 'No Supplier'})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Color</label>
                      <select
                        name="color_id"
                        value={variantFormData.color_id}
                        onChange={handleVariantInputChange}
                      >
                        <option value="">No Color</option>
                        {productColors.map(pc => (
                          <option key={pc.color.id} value={pc.color.id}>
                            {pc.color.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Size</label>
                      <select
                        name="size_id"
                        value={variantFormData.size_id}
                        onChange={handleVariantInputChange}
                      >
                        <option value="">No Size</option>
                        {sizes.map(size => (
                          <option key={size.id} value={size.id}>
                            {size.name} ({size.value} {size.unit})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Stock Price *</label>
                      <input
                        type="number"
                        name="stock_price"
                        value={variantFormData.stock_price}
                        onChange={handleVariantInputChange}
                        placeholder="0.00"
                        step="0.01"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Stock Quantity *</label>
                      <input
                        type="number"
                        name="stock_quantity"
                        value={variantFormData.stock_quantity}
                        onChange={handleVariantInputChange}
                        placeholder="0"
                        min="0"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Current Quantity {editingVariant ? '*' : ''}</label>
                      <input
                        type="number"
                        name="current_quantity"
                        value={variantFormData.current_quantity}
                        onChange={handleVariantInputChange}
                        placeholder="0"
                        min="0"
                        disabled={!editingVariant}
                        required={!!editingVariant}
                      />
                      <small className="form-description">
                        {editingVariant
                          ? 'Update only for manual corrections (for example, damaged or lost items).'
                          : 'Current quantity is set automatically from stock quantity when creating variant.'}
                      </small>
                    </div>

                    <div className="form-group">
                      <label>Current Quantity Update Reason</label>
                      <textarea
                        name="current_quantity_update_reason"
                        value={variantFormData.current_quantity_update_reason}
                        onChange={handleVariantInputChange}
                        placeholder="Provide reason when current quantity is changed"
                        rows={3}
                        disabled={!editingVariant}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Shipping Charge</label>
                      <input
                        type="number"
                        name="shipping_charge"
                        value={variantFormData.shipping_charge}
                        onChange={handleVariantInputChange}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                      />
                    </div>

                    <div className="form-group">
                      <label>Value Factor</label>
                      <input
                        type="number"
                        name="value_factor"
                        value={variantFormData.value_factor}
                        onChange={handleVariantInputChange}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                      />
                    </div>

                    <div className="form-group">
                      <label>Discount Percent</label>
                      <input
                        type="number"
                        name="discount_percent"
                        value={variantFormData.discount_percent}
                        onChange={handleVariantInputChange}
                        placeholder="0"
                        step="0.01"
                        min="0"
                        max="100"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        name="is_featured"
                        checked={Boolean(variantFormData.is_featured)}
                        onChange={(e) => setVariantFormData(prev => ({ ...prev, is_featured: e.target.checked }))}
                      />
                      Featured — show this variant at the top of the home page
                    </label>
                  </div>

                  <div className="form-group">
                    <label>Variant SKU (Optional)</label>
                    <div className="sku-control">
                      <input
                        type="text"
                        name="variant_sku"
                        value={variantFormData.variant_sku}
                        onChange={handleVariantInputChange}
                        placeholder={`Auto-generated if empty`}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-outline"
                        onClick={copySKUToClipboard}
                      >
                        Copy SKU
                      </button>
                    </div>
                    <small className="form-description">Suggested: {suggestedVariantSKU}</small>
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="btn btn-success">
                      {editingVariant ? 'Update Variant' : 'Create Variant'}
                    </button>
                    {editingVariant && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={resetVariantForm}
                      >
                        Cancel Edit
                      </button>
                    )}
                  </div>
                </form>
              </div>

              {/* Variants List */}
              {productVariants.length > 0 && (
                <div className="variants-list">
                  <h3>Product Variants ({productVariants.length})</h3>
                  <table className="variants-table">
                    <thead>
                      <tr>
                        <th>Color</th>
                        <th>Size</th>
                        <th>Shipment</th>
                        <th>Stock Price</th>
                        <th>Final Price</th>
                        <th>Stock Quantity</th>
                        <th>Current Quantity</th>
                        <th>SKU</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productVariants.map(variant => (
                        <tr key={variant.id}>
                          <td>{variant.color?.name || 'N/A'}</td>
                          <td>{variant.size ? `${variant.size.name} (${variant.size.value} ${variant.size.unit})` : 'N/A'}</td>
                          <td>{variant.shipment?.shipment_id || '-'}</td>
                          <td>
                            {variant.stock_price ? `₹${variant.stock_price}` : 'N/A'}
                          </td>
                          <td>
                            {variant.final_sp_unit_discount ? `₹${variant.final_sp_unit_discount}` : 'N/A'}
                          </td>
                          <td>{variant.stock_quantity ?? 0}</td>
                          <td>{variant.current_quantity ?? variant.stock_quantity ?? 0}</td>
                          <td>{variant.variant_sku}</td>
                          <td className="actions">
                            <button
                              className="btn btn-sm btn-info"
                              onClick={() => handleEditVariant(variant)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDeleteVariant(variant.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {editingVariant && (
                <div className="variants-list">
                  <h3>Current Quantity Update History</h3>
                  {loadingAdjustmentHistory ? (
                    <p>Loading history...</p>
                  ) : variantAdjustmentHistory.length === 0 ? (
                    <p>No current quantity adjustments recorded yet.</p>
                  ) : (
                    <table className="variants-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Updated By</th>
                          <th>Previous Qty</th>
                          <th>New Qty</th>
                          <th>Delta</th>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {variantAdjustmentHistory.map((entry) => (
                          <tr key={entry.id}>
                            <td>{entry.created_at ? new Date(entry.created_at).toLocaleString() : '-'}</td>
                            <td>{entry.updated_by_name || entry.updated_by_user_id}</td>
                            <td>{entry.previous_quantity}</td>
                            <td>{entry.new_quantity}</td>
                            <td>{entry.quantity_delta}</td>
                            <td>{entry.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {productVariants.length === 0 && (
                <div className="empty-state">
                  <p>No variants yet. Create your first variant or use bulk creation!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {products.length === 0 && !showForm && (
        <div className="empty-state">
          <p>No products yet. Create your first product!</p>
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            + Add Product
          </button>
        </div>
      )}

      {confirmDialog.show && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '540px' }}>
            <div className="modal-header">
              <h2>{confirmDialog.title || 'Confirm Action'}</h2>
              <button className="btn btn-sm btn-secondary" onClick={closeConfirmDialog}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 0 }}>{confirmDialog.message}</p>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={closeConfirmDialog}>Cancel</button>
              <button type="button" className={confirmActionClass} onClick={executeConfirmDialogAction}>
                {confirmDialog.confirmText || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductManager;
